import { isFreePlanSync } from '@/services/subscriptionService';

// Default subscription structure for new users
// The actual product_id will be fetched from Firebase
// IMPORTANT: The 'plan' field is required by the backend AI processing worker.
// Without it, the worker treats the user as "free plan" and skips AI processing.
export const DEFAULT_FREE_SUBSCRIPTION = {
  product_id: null, // Will be set from Firebase
  price_id: null,
  stripe_customer_id: null,
  status: 'active',
  plan: 'FREE', // Required by backend AI worker to determine processing eligibility
  plan_type: 'rate_based' as const,
  card_last_four_digits: null,
  scheduled_to_end: false,
  scheduled_to_downgrade: null,
  current_period_end: null,
  product_name: 'FREE',
} as const;

// Helper to check if a product_id represents a free plan
// Uses Firebase data when available
export const isFreePlan = (productId: string | null | undefined): boolean => {
  return isFreePlanSync(productId);
};

// Helper to check if a product_id is valid (not null/undefined)
export const isValidProductId = (productId: string | null | undefined): boolean => {
  return !!productId;
};

// Trial detection configuration
export const TRIAL_DETECTION = {
  // Check if subscription is a trial based on corrected behavior
  // Trials have product names containing "trial" (case insensitive) like "Pro Trial", "Free Trial"
  isTrialSubscription: (subscription: {
    scheduled_to_end?: boolean | null;
    product_name?: string | null;
    status?: string | null;
    id?: string | null;
    current_period_end?: string | null;
  }): boolean => {
    return Boolean(
      subscription.scheduled_to_end === true &&
      subscription.product_name &&
      subscription.product_name.toLowerCase().includes('trial') && // Check for "trial" in product name
      subscription.status === 'active' &&
      (subscription.id === '' || subscription.id === null || subscription.id === undefined) &&
      subscription.current_period_end, // Must have an end date
    );
  },

  // Helper function to check if product name indicates a trial
  isTrialProductName: (productName: string | null | undefined): boolean => {
    return productName ? productName.toLowerCase().includes('trial') : false;
  },

  // All conditions that must be met for a subscription to be considered a trial
  requiredConditions: {
    scheduledToEnd: true,
    status: 'active' as const,
    emptySubscriptionId: true, // subscription.id should be empty string or null
    productNameContainsTrial: true, // Product name must contain "trial" (case insensitive)
  },
} as const;
